import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API from '../config';
import { useTheme } from '../context/ThemeContext';
import { Helmet } from 'react-helmet-async';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `KES ${parseFloat(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtK = (n) => {
  const v = parseFloat(n || 0);
  return v >= 1_000_000
    ? `KES ${(v / 1_000_000).toFixed(1)}M`
    : v >= 1000
    ? `KES ${(v / 1000).toFixed(1)}K`
    : `KES ${v.toFixed(0)}`;
};
const displayName = (l) => (l.username ? `@${l.username}` : 'Anonymous');

const SYSTEM_ACCOUNTS = ['admin', 'administrator', 'system', 'test', 'demo'];
const MIN_TRADES = 5;

const MEDAL = {
  colors: ['#F0A500', '#9B9B9B', '#C07040'],
  labels: ['Gold', 'Silver', 'Bronze'],
  rings:  ['rgba(240,165,0,0.18)', 'rgba(155,155,155,0.12)', 'rgba(192,112,64,0.14)'],
};

// ─── Avatar (individual) ─────────────────────────────────────────────────────
const Avatar = ({ name, size = 36, rank = -1 }) => {
  const { T } = useTheme();
  const initials = (name || '??').split(/[_\s]/).map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  const isMedal = rank >= 0 && rank < 3;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: isMedal ? MEDAL.rings[rank] : T.surfaceHi,
      border: `1.5px solid ${isMedal ? MEDAL.colors[rank] : T.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.33, fontWeight: 600,
      color: isMedal ? MEDAL.colors[rank] : T.textMuted,
      fontFamily: "'Syne', sans-serif",
    }}>
      {initials}
    </div>
  );
};

// ─── Chama Avatar (group icon) ───────────────────────────────────────────────
const ChamaAvatar = ({ name, size = 36, rank = -1 }) => {
  const { T } = useTheme();
  const initials = (name || '??').slice(0, 2).toUpperCase();
  const isMedal = rank >= 0 && rank < 3;
  const accent = isMedal ? MEDAL.colors[rank] : 'rgba(167,139,250,0.8)';
  const bg     = isMedal ? MEDAL.rings[rank]  : 'rgba(167,139,250,0.12)';
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.28,
      flexShrink: 0, background: bg,
      border: `1.5px solid ${isMedal ? accent + '66' : 'rgba(167,139,250,0.3)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.33, fontWeight: 700,
      color: accent, fontFamily: "'Syne', sans-serif",
    }}>
      {initials}
    </div>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const Skeleton = ({ w = '100%', h = 16, r = 6 }) => {
  const { T } = useTheme();
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: `linear-gradient(90deg,${T.surfaceHi} 25%,${T.border} 50%,${T.surfaceHi} 75%)`,
      backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
    }} />
  );
};

// ─── WinRate cell ─────────────────────────────────────────────────────────────
const WinRateCell = ({ rate, trades, T }) => {
  const pct = parseFloat(rate || 0);
  const n   = parseInt(trades || 0);
  if (n === 0) return <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: T.textDim }}>—</span>;
  const lowSample = n < MIN_TRADES;
  const barColor  = pct >= 50 ? T.yes : T.no;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: barColor }}>
          {pct.toFixed(0)}%
        </span>
        {lowSample && (
          <span title={`Only ${n} trade${n !== 1 ? 's' : ''} — small sample`}
            style={{ fontSize: 9, fontFamily: "'Outfit',sans-serif", color: T.textDim, background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 3, padding: '1px 4px', letterSpacing: '0.04em', cursor: 'default' }}>
            {n}T
          </span>
        )}
      </div>
      <div style={{ height: 2, background: T.border, borderRadius: 1, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: barColor, opacity: 0.55, borderRadius: 1, transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
};

// ─── ROI Badge ────────────────────────────────────────────────────────────────
const RoiBadge = ({ roi, T, large = false }) => {
  if (roi === null || roi === undefined) {
    return <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: T.textDim }}>—</span>;
  }
  const v     = parseFloat(roi);
  const color = v >= 0 ? T.yes : T.no;
  return (
    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: large ? 15 : 13, fontWeight: 700, color }}>
      {v >= 0 ? '+' : ''}{v.toFixed(1)}%
    </span>
  );
};

// ─── Consistency badge ────────────────────────────────────────────────────────
const ConsistencyBadge = ({ score, T }) => {
  if (score === null || score === undefined) return null;
  const v = parseFloat(score);
  const color = v >= 0 ? 'rgba(22,199,132,0.7)' : 'rgba(234,57,67,0.7)';
  return (
    <span title="Consistency score: ROI × ln(weeks active). Rewards sustained performance over lucky trades."
      style={{ display: 'inline-block', fontSize: 9, fontFamily: "'DM Mono',monospace", color, background: v >= 0 ? 'rgba(22,199,132,0.08)' : 'rgba(234,57,67,0.08)', border: `1px solid ${color}33`, borderRadius: 4, padding: '1px 5px', letterSpacing: '0.04em', cursor: 'default' }}>
      {v >= 0 ? '↑' : '↓'} {Math.abs(v).toFixed(1)}
    </span>
  );
};

// ─── Join Request button ──────────────────────────────────────────────────────
const JoinBtn = ({ chamaId, chamaName, requested, onRequest, T, small = false }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(requested);

  const handle = async (e) => {
    e.stopPropagation();
    if (done || loading) return;
    setLoading(true);
    await onRequest(chamaId, chamaName);
    setDone(true);
    setLoading(false);
  };

  const pad   = small ? '4px 10px' : '6px 14px';
  const fsize = small ? 10 : 11;

  if (done) {
    return (
      <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: fsize, color: T.textDim, border: `1px solid ${T.border}`, borderRadius: 6, padding: pad, whiteSpace: 'nowrap' }}>
        Requested ✓
      </span>
    );
  }
  return (
    <button onClick={handle} disabled={loading}
      style={{ fontFamily: "'Outfit',sans-serif", fontSize: fsize, fontWeight: 500, color: '#a78bfa', background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6, padding: pad, cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s', opacity: loading ? 0.6 : 1 }}>
      {loading ? '…' : '+ Request to Join'}
    </button>
  );
};

// ─── Periods ─────────────────────────────────────────────────────────────────
const PERIODS = [
  { label: 'All time',   param: 'all' },
  { label: 'This month', param: 'month' },
  { label: 'This week',  param: 'week' },
];

// ─── Main component ───────────────────────────────────────────────────────────
export default function Leaderboard() {
  const { T }    = useTheme();
  const navigate = useNavigate();

  const [leaders,       setLeaders]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [period,        setPeriod]        = useState(0);
  const [search,        setSearch]        = useState('');
  const [view,          setView]          = useState('individual');
  const [chamaLeaders,  setChamaLeaders]  = useState([]);
  const [chamaLoading,  setChamaLoading]  = useState(false);
  const [requested,     setRequested]     = useState(new Set());  // chama IDs with pending requests

  const token = localStorage.getItem('token');

  // ── Individual leaderboard ──
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/leaderboard`, { params: { period: PERIODS[period].param } })
      .then((r) => {
        const clean = (r.data || []).filter(
          (u) => !SYSTEM_ACCOUNTS.includes((u.username || '').toLowerCase()) && parseInt(u.total_bets || 0) > 0
        );
        setLeaders(clean);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [period]);

  // ── Chama leaderboard — also respects period ──
  useEffect(() => {
    if (view !== 'chama') return;
    setChamaLoading(true);
    axios.get(`${API}/api/chamas/leaderboard/global`, {
      params: { period: PERIODS[period].param },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => setChamaLeaders(r.data?.leaderboard || []))
      .catch(console.error)
      .finally(() => setChamaLoading(false));
  }, [view, period]);

  // ── Join request handler ──
  const handleJoinRequest = useCallback(async (chamaId, chamaName) => {
    if (!token) { navigate('/login'); return; }
    try {
      await axios.post(
        `${API}/api/chamas/${chamaId}/join-request`,
        { message: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequested((prev) => new Set([...prev, chamaId]));
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not send request';
      alert(msg);
    }
  }, [token, navigate]);

  const visible     = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? leaders.filter((l) => (l.username || '').toLowerCase().includes(q)) : leaders;
  }, [leaders, search]);

  const topThree = visible.slice(0, 3);
  const rest     = visible.slice(3);

  const chamaTop3 = chamaLeaders.slice(0, 3);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Outfit','DM Sans',sans-serif", position: 'relative', overflowX: 'hidden' }}>

      <Helmet>
        <title>Leaderboard | Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
        <meta name="description" content="See the top predictors and chamas on Mino Markets — ranked by winnings, win rate, and ROI." />
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content="Leaderboard | Mino Markets" />
        <meta property="og:description" content="Who's winning on Mino Markets? See the top predictors and chamas by winnings and ROI." />
        <meta property="og:url"         content="https://minomarkets.app/leaderboard" />
        <meta property="og:image"       content="https://minomarkets.app/preview.png" />
        <meta name="twitter:card"       content="summary_large_image" />
      </Helmet>

      <style>{`
        @keyframes shimmer    { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes fadeUp     { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes auroraShift  { 0%{transform:rotate(-4deg) scaleX(1);opacity:1} 50%{transform:rotate(2deg) scaleX(1.08);opacity:0.85} 100%{transform:rotate(-6deg) scaleX(0.96);opacity:1} }
        @keyframes auroraShift2 { 0%{transform:rotate(6deg) scaleX(1);opacity:0.8} 50%{transform:rotate(-3deg) scaleX(1.1);opacity:1} 100%{transform:rotate(8deg) scaleX(0.92);opacity:0.75} }
        .mino-aurora { position:fixed; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
        .mino-aurora::before { content:''; position:absolute; width:140%; height:55%; top:-18%; left:-20%; background:conic-gradient(from 200deg at 50% 50%,transparent 0deg,rgba(55,114,255,0.07) 40deg,rgba(22,199,132,0.05) 80deg,rgba(124,58,237,0.06) 120deg,transparent 160deg); filter:blur(60px); animation:auroraShift 20s ease-in-out infinite alternate; }
        .mino-aurora::after  { content:''; position:absolute; width:80%;  height:40%; bottom:5%; right:-10%;  background:conic-gradient(from 30deg at 50% 50%,transparent 0deg,rgba(22,199,132,0.04) 50deg,rgba(55,114,255,0.04) 100deg,transparent 150deg); filter:blur(70px); animation:auroraShift2 26s ease-in-out infinite alternate; }
        .mino-dot-grid { position:fixed; inset:0; pointer-events:none; z-index:0; background-image:radial-gradient(circle,rgba(255,255,255,0.035) 1px,transparent 1px); background-size:40px 40px; mask-image:radial-gradient(ellipse 85% 85% at 50% 40%,black 40%,transparent 100%); }
        .mino-nav-link { font-family:'Outfit',sans-serif; font-size:13px; cursor:pointer; padding:5px 12px; border-radius:6px; transition:all 0.2s; font-weight:400; letter-spacing:0.01em; background:transparent; border:none; }
        .lb-row { transition:background 0.15s; border-radius:8px; cursor:default; }
        .lb-row:hover { background:rgba(255,255,255,0.03) !important; }
        .lb-period-btn { border:none; cursor:pointer; font-family:'Outfit',sans-serif; font-size:11px; letter-spacing:0.1em; text-transform:uppercase; padding:6px 14px; border-radius:6px; transition:all 0.2s; }
        .lb-search { background:transparent; border-radius:8px; padding:8px 14px 8px 36px; font-family:'Outfit',sans-serif; font-size:13px; outline:none; width:180px; transition:border-color 0.2s; }
        .lb-search-wrap { position:relative; }
        .lb-search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); pointer-events:none; }
        * { box-sizing:border-box; margin:0; padding:0; }
        ::selection { background:rgba(255,255,255,0.1); }
        ::-webkit-scrollbar { width:3px; }
        ::-webkit-scrollbar-thumb { border-radius:2px; }

        /* Chama podium card hover */
        .chama-podium-card { transition:transform 0.15s, border-color 0.15s; }
        .chama-podium-card:hover { transform:translateY(-2px); }

        @media (max-width:640px) {
          .lb-cols { grid-template-columns: 28px minmax(0,1fr) 90px !important; gap: 10px !important; }
.lb-col-winrate { display: none !important; }
.lb-main-pad { padding: 40px 16px 80px !important; }
          .lb-nav-pad      { padding:0 20px !important; }
          .lb-main-pad     { padding:60px 20px 80px !important; }
          .lb-podium       { grid-template-columns:1fr !important; }
          .lb-controls     { flex-direction:column !important; align-items:flex-start !important; }
          .lb-search       { width:100% !important; }
          .chama-col-pool  { display:none !important; }
          .chama-col-wr    { display:none !important; }
          .chama-col-join  { display:none !important; }
          .chama-podium    { grid-template-columns:1fr !important; }
        }
      `}</style>

      <div className="mino-aurora" />
      <div className="mino-dot-grid" />

      {/* ── Nav ── */}
      <nav style={{ position:'sticky', top:0, zIndex:100, height:60, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 48px', background:`${T.bg}cc`, backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', borderBottom:`1px solid ${T.border}` }} className="lb-nav-pad">
        <div onClick={() => navigate('/markets')} style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily:"'Syne',sans-serif", fontSize:15, fontWeight:600, letterSpacing:'-0.3px', color:T.text }}>Mino</span>
        </div>
        <div style={{ display:'flex', gap:2, alignItems:'center' }}>
          {[['Markets','/markets'],['Dashboard','/dashboard']].map(([l,p]) => (
            <span key={p} className="mino-nav-link" style={{ color:T.textMuted }}
              onClick={() => navigate(p)}
              onMouseEnter={e => (e.currentTarget.style.color = T.text)}
              onMouseLeave={e => (e.currentTarget.style.color = T.textMuted)}>
              {l}
            </span>
          ))}
        </div>
        <button onClick={() => navigate(-1)}
          style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 18px', cursor:'pointer', fontSize:13, fontFamily:"'Outfit',sans-serif", transition:'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.color=T.text; e.currentTarget.style.borderColor=T.textMuted; }}
          onMouseLeave={e => { e.currentTarget.style.color=T.textMuted; e.currentTarget.style.borderColor=T.border; }}>
          Back
        </button>
      </nav>

      {/* ── Main ── */}
      <div style={{ position:'relative', zIndex:1, maxWidth:860, margin:'0 auto', padding:'80px 48px 100px', animation:'fadeUp 0.4s ease' }} className="lb-main-pad">

        {/* Header */}
        <div style={{ paddingBottom:40 }}>
          <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, fontWeight:500, letterSpacing:'0.16em', textTransform:'uppercase', color:T.textDim, marginBottom:24 }}>
            Leaderboard
            {!loading && view === 'individual' && (
              <span style={{ marginLeft:12, color:T.textDim, fontWeight:400 }}>{visible.length}</span>
            )}
          </p>
          <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:'clamp(36px,5vw,60px)', fontWeight:700, lineHeight:1.04, letterSpacing:'-2.5px', color:T.text, marginBottom:16 }}>
            {view === 'chama' ? 'Top chamas.' : 'Top predictors.'}
          </h1>
          <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:15, color:T.textMuted, fontWeight:300, lineHeight:1.7, maxWidth:460 }}>
            {view === 'chama'
              ? <>Ranked by <strong style={{ color:T.text, fontWeight:500 }}>consistency score</strong> — ROI × time active. Min {MIN_TRADES} trades to qualify.</>
              : <>Ranked by total winnings. Win rate marked <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:T.textDim, background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:3, padding:'1px 5px' }}>nT</span> below {MIN_TRADES} trades.</>
            }
          </p>
        </div>

        {/* View toggle */}
        <div style={{ display:'flex', gap:4, background:T.surfaceHi, borderRadius:10, padding:4, border:`1px solid ${T.border}`, marginBottom:24, width:'fit-content' }}>
          {[['individual','👤 Individuals'],['chama','👤👤👤 Chamas']].map(([v,label]) => (
            <button key={v} onClick={() => setView(v)} style={{
              padding:'8px 20px', borderRadius:7, border:'none', cursor:'pointer',
              fontFamily:"'Outfit',sans-serif", fontSize:13, fontWeight:view===v?600:400,
              background:view===v?T.surface:'transparent',
              color:view===v?T.text:T.textDim,
              transition:'all 0.15s',
            }}>{label}</button>
          ))}
        </div>

        {/* Controls: period tabs + search */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:48, flexWrap:'wrap' }} className="lb-controls">
          <div style={{ display:'flex', gap:3, background:T.surfaceHi, borderRadius:8, padding:3 }}>
            {PERIODS.map((p, i) => (
              <button key={p.param} className="lb-period-btn"
                style={{ background:period===i?T.surface||T.bg:'transparent', color:period===i?T.text:T.textDim, border:period===i?`1px solid ${T.border}`:'1px solid transparent' }}
                onClick={() => setPeriod(i)}>
                {p.label}
              </button>
            ))}
          </div>
          {view === 'individual' && (
            <div className="lb-search-wrap">
              <svg className="lb-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input className="lb-search" placeholder="Find a predictor…" value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ border:`1px solid ${T.border}`, color:T.text }} />
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ── CHAMA LEADERBOARD ──                                          */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {view === 'chama' && (
          <div>
            {chamaLoading ? (
              /* skeleton */
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {[1,2,3,4,5].map(i => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'52px 1fr 60px 80px 60px 80px 100px', gap:16, padding:'18px 6px', borderBottom:`1px solid ${T.border}`, alignItems:'center' }}>
                    <Skeleton w={20} h={12} r={3} />
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}><Skeleton w={32} h={32} r={8} /><Skeleton w="55%" h={13} /></div>
                    <Skeleton w={30} h={11} /><Skeleton w={55} h={11} /><Skeleton w={30} h={11} />
                    <Skeleton w={50} h={11} /><Skeleton w={70} h={14} />
                  </div>
                ))}
              </div>
            ) : chamaLeaders.length === 0 ? (
              <div style={{ padding:'80px 0', textAlign:'center' }}>
                <p style={{ fontFamily:"'Syne',sans-serif", fontSize:14, color:T.textDim }}>No chamas have qualified yet (min {MIN_TRADES} trades)</p>
                <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:12, color:T.textDim, marginTop:8 }}>Check back after more trading activity</p>
              </div>
            ) : (
              <>
                {/* ── Chama Podium (top 3) ── */}
                {chamaTop3.length === 3 && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:48 }} className="chama-podium">
                    {chamaTop3.map((c, i) => {
                      const mc      = MEDAL.colors[i];
                      const roi     = c.roi_pct !== null ? parseFloat(c.roi_pct) : null;
                      const roiClr  = roi === null ? T.textDim : roi >= 0 ? T.yes : T.no;
                      const pool    = parseFloat(c.pool_size || 0);
                      return (
                        <div key={c.id} className="chama-podium-card" style={{
                          background:T.surface||T.surfaceHi,
                          border:`1px solid ${i===0?mc+'44':T.border}`,
                          borderRadius:14, padding:'20px 14px 18px',
                          textAlign:'center', position:'relative',
                        }}>
                          {/* Medal label */}
                          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:10, fontWeight:700, letterSpacing:'0.18em', textTransform:'uppercase', color:mc, marginBottom:12 }}>
                            {MEDAL.labels[i]}
                          </div>

                          {/* Group avatar */}
                          <div style={{ display:'flex', justifyContent:'center', marginBottom:10 }}>
                            <ChamaAvatar name={c.name} size={48} rank={i} />
                          </div>

                          {/* Chama name — no @ prefix */}
                          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:14, fontWeight:600, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:8 }}>
                            {c.name || 'Unnamed'}
                          </div>

                          {/* Stats line: members · ROI · pool */}
                          <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:T.textDim, marginBottom:12, lineHeight:1.6 }}>
                            <span>{c.member_count || 0} members</span>
                            {roi !== null && (
                              <>
                                <span style={{ margin:'0 6px', opacity:0.4 }}>·</span>
                                <span style={{ color:roiClr, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                                  {roi >= 0 ? '+' : ''}{roi.toFixed(1)}% ROI
                                </span>
                              </>
                            )}
                            {pool > 0 && (
                              <>
                                <span style={{ margin:'0 6px', opacity:0.4 }}>·</span>
                                <span style={{ fontFamily:"'DM Mono',monospace" }}>{fmtK(pool)} pool</span>
                              </>
                            )}
                          </div>

                          {/* Consistency score */}
                          {c.consistency_score !== null && (
                            <div style={{ marginBottom:12 }}>
                              <ConsistencyBadge score={c.consistency_score} T={T} />
                            </div>
                          )}

                          {/* Request to Join CTA */}
                          <JoinBtn
                            chamaId={c.id}
                            chamaName={c.name}
                            requested={requested.has(c.id)}
                            onRequest={handleJoinRequest}
                            T={T}
                            small
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ height:1, background:T.border, marginBottom:32 }} />

                {/* ── Chama column headers ── */}
                {/* Columns: #  Chama  Members  Pool Size  Trades  Win Rate  ROI%  Join */}
                <div style={{ display:'grid', gridTemplateColumns:'44px minmax(0,1fr) 58px 90px 52px 80px 80px 120px', gap:14, paddingBottom:12, marginBottom:4 }}>
                  {[
                    ['#',        ''],
                    ['Chama',    ''],
                    ['Members',  ''],
                    ['Pool',     'chama-col-pool'],
                    ['Trades',   ''],
                    ['Win Rate', 'chama-col-wr'],
                    ['ROI%',     ''],
                    ['',         'chama-col-join'],
                  ].map(([h, cls], idx) => (
                    <span key={idx} className={cls} style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, fontWeight:500, letterSpacing:'0.12em', textTransform:'uppercase', color:T.textDim, textAlign: idx >= 6 ? 'right' : 'left' }}>{h}</span>
                  ))}
                </div>

                {/* ── Chama rows ── */}
                {chamaLeaders.map((c, i) => {
                  const isTop   = i < 3;
                  const roi     = c.roi_pct !== null ? parseFloat(c.roi_pct) : null;
                  const roiClr  = roi === null ? T.textDim : roi >= 0 ? T.yes : T.no;
                  return (
                    <div key={c.id} className="lb-row"
                      style={{ display:'grid', gridTemplateColumns:'44px minmax(0,1fr) 58px 90px 52px 80px 80px 120px', gap:14, padding:'16px 6px', borderBottom:`1px solid ${T.border}`, alignItems:'center', background:'transparent' }}>

                      {/* Rank */}
                      <span style={{ fontFamily:"'Syne',sans-serif", fontSize:11, fontWeight:600, color:isTop?MEDAL.colors[i]:T.textDim }}>
                        {String(i+1).padStart(2,'0')}
                      </span>

                      {/* Chama name + consistency badge */}
                      <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                        <ChamaAvatar name={c.name} size={30} rank={i} />
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:isTop?14:13, fontWeight:i===0?700:500, color:i===0?T.text:T.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {c.name || 'Unnamed'}
                          </div>
                          {c.consistency_score !== null && (
                            <div style={{ marginTop:2 }}>
                              <ConsistencyBadge score={c.consistency_score} T={T} />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Members */}
                      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:12, color:T.textDim }}>{c.member_count || 0}</span>

                      {/* Pool size */}
                      <span className="chama-col-pool" style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:T.textDim }}>{fmtK(c.pool_size)}</span>

                      {/* Trades */}
                      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:12, color:T.textDim }}>{parseInt(c.trade_count||0)}</span>

                      {/* Win rate */}
                      <div className="chama-col-wr">
                        <WinRateCell rate={c.win_rate} trades={c.trade_count} T={T} />
                      </div>

                      {/* ROI% */}
                      <div style={{ textAlign:'right' }}>
                        <RoiBadge roi={roi} T={T} large={i===0} />
                      </div>

                      {/* Request to Join */}
                      <div className="chama-col-join" style={{ display:'flex', justifyContent:'flex-end' }}>
                        <JoinBtn
                          chamaId={c.id}
                          chamaName={c.name}
                          requested={requested.has(c.id)}
                          onRequest={handleJoinRequest}
                          T={T}
                          small
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Chama footer */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:40, flexWrap:'wrap', gap:8 }}>
                  <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:T.textDim, letterSpacing:'0.08em' }}>
                    {chamaLeaders.length} chama{chamaLeaders.length !== 1 ? 's' : ''} · min {MIN_TRADES} trades to qualify
                  </p>
                  <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:T.textDim, letterSpacing:'0.06em' }}>
                    ranked by consistency score · ROI × ln(weeks active)
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ── INDIVIDUAL LEADERBOARD ──                                     */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {view === 'individual' && (
          <>
            {/* Top 3 podium */}
            {!loading && !search && topThree.length === 3 && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:48 }} className="lb-podium">
                {topThree.map((leader, i) => {
                  const mc = MEDAL.colors[i];
                  const wr = parseFloat(leader.win_rate || 0).toFixed(0);
                  return (
                    <div key={leader.id} style={{ background:T.surface||T.surfaceHi, border:`1px solid ${i===0?mc+'44':T.border}`, borderRadius:14, padding:'20px 14px 18px', textAlign:'center', position:'relative' }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontSize:10, fontWeight:700, letterSpacing:'0.18em', textTransform:'uppercase', color:mc, marginBottom:12 }}>
                        {MEDAL.labels[i]}
                      </div>
                      <div style={{ display:'flex', justifyContent:'center', marginBottom:10 }}>
                        <Avatar name={leader.username} size={44} rank={i} />
                      </div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontSize:13, fontWeight:600, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:8 }}>
                        {displayName(leader)}
                      </div>
                      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:16, fontWeight:700, color:mc }}>
                        {fmt(leader.total_winnings)}
                      </div>
                      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:T.textDim, marginTop:5 }}>
                        {parseInt(leader.total_bets||0)} trades · {wr}% win rate
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ height:1, background:T.border, marginBottom:32 }} />

            {/* Column headers */}
            {!loading && visible.length > 0 && (
              <div className="lb-cols" style={{ display:'grid', gridTemplateColumns:'52px minmax(0,1fr) 72px 120px 130px', gap:20, paddingBottom:12, marginBottom:4 }}>
                {[['#',''],['Predictor',''],['Trades',''],['Win rate','lb-col-winrate'],['Winnings','']].map(([h,cls],idx) => (
                  <span key={h} className={cls} style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, fontWeight:500, letterSpacing:'0.12em', textTransform:'uppercase', color:T.textDim, textAlign:idx===4?'right':'left' }}>{h}</span>
                ))}
              </div>
            )}

            {/* Rows */}
            {loading ? (
              <div>
                {[1,2,3,4,5,6,7].map((i) => (
                  <div key={i} className="lb-cols" style={{ display:'grid', gridTemplateColumns:'52px minmax(0,1fr) 72px 120px 130px', gap:20, padding:'20px 0', borderBottom:`1px solid ${T.border}`, alignItems:'center' }}>
                    <Skeleton w={20} h={12} r={3} />
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}><Skeleton w={32} h={32} r={99} /><Skeleton w="55%" h={13} /></div>
                    <Skeleton w={28} h={11} />
                    <Skeleton w="80%" h={6} r={2} />
                    <div style={{ display:'flex', justifyContent:'flex-end' }}><Skeleton w={90} h={14} /></div>
                  </div>
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div style={{ padding:'80px 0', textAlign:'center' }}>
                <p style={{ fontFamily:"'Syne',sans-serif", fontSize:14, color:T.textDim }}>
                  {search.trim() ? `No predictor matching "${search.trim()}"` : 'No predictors yet for this period'}
                </p>
              </div>
            ) : (
              <div>
                {visible.map((leader, i) => {
                  const isTop  = i < 3;
                  const trades = parseInt(leader.total_bets || 0);
                  return (
                    <div key={leader.id} className="lb-row lb-cols" style={{ display:'grid', gridTemplateColumns:'52px minmax(0,1fr) 72px 120px 130px', gap:20, padding:'18px 6px', borderBottom:`1px solid ${T.border}`, alignItems:'center', background:'transparent' }}>
                      <span style={{ fontFamily:"'Syne',sans-serif", fontSize:11, fontWeight:600, letterSpacing:'0.06em', color:isTop?MEDAL.colors[i]:T.textDim }}>
                        {String(i+1).padStart(2,'0')}
                      </span>
                      <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
                        <Avatar name={leader.username} size={32} rank={i} />
                        <span style={{ fontFamily:"'Syne',sans-serif", fontSize:isTop?15:14, fontWeight:i===0?700:isTop?600:500, color:i===0?T.text:isTop?T.textMuted:T.textDim, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {displayName(leader)}
                        </span>
                      </div>
                      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:12, color:T.textDim }}>{trades}</span>
                      <div className="lb-col-winrate">
                        <WinRateCell rate={leader.win_rate} trades={leader.total_bets} T={T} />
                      </div>
                      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:i===0?15:isTop?14:13, fontWeight:700, color:i===0?T.text:isTop?T.textMuted:T.textDim, textAlign:'right' }}>
                        {fmt(leader.total_winnings)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer */}
            {!loading && visible.length > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:40, flexWrap:'wrap', gap:8 }}>
                <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:T.textDim, letterSpacing:'0.08em' }}>
                  {visible.length} predictor{visible.length!==1?'s':''} · updated in real time
                </p>
                <p style={{ fontFamily:"'Outfit',sans-serif", fontSize:11, color:T.textDim, letterSpacing:'0.06em' }}>
                  win rate verified at {MIN_TRADES}+ trades
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}